import { Settings } from '@mui/icons-material';
import React, { useCallback, useMemo, useState } from 'react';
import {
  SubprojectBuildingDetails,
  TechnologyParameter,
} from '../../stores/SubprojectStore.ts';
import { DecarbContentLayout } from '../components/DecarbContentLayout.tsx';
import { DecarbOverviewHeader } from '../components/DecarbOverviewHeader.tsx';
import { FilterMenu, SearchBox, SortMenu } from '../components/TechToolbar';
import {
  EnergyKind,
  SpecializedEquipmentWithMetrics,
  Tab,
  TechSortDimensions,
  TechSortState,
  sortTechItemsByState,
} from '../types';
import { isPassiveTechnology, storedCategoryCapitalCost } from '../utils.ts';
import { calculateEnergyDemand } from '../utils/calculations';
import { SpecializedUnit } from './SpecializedUnit.tsx';

interface SpecializedForBuildingDetailsProps {
  subproject: SubprojectBuildingDetails;
  // The stored cost figures predate the selections on screen (unsaved edit).
  costsArePending?: boolean;
  tab: Tab;
  specializedEquipment: SpecializedEquipmentWithMetrics[];
  onAddEquipment: (equipmentId: string, limit: number) => void;
  onChangeEquipmentCount: (equipmentId: string, newCount: number) => void;
  onCopyConventionalAlternative: (tab: Tab) => void;
  emptyText: React.ReactNode;
  isLoading?: boolean;
  readOnly?: boolean;
}

export const SpecializedForBuildingDetails: React.FC<
  SpecializedForBuildingDetailsProps
> = ({
  subproject,
  costsArePending = false,
  tab,
  specializedEquipment,
  onAddEquipment,
  onChangeEquipmentCount,
  emptyText,
  isLoading,
  readOnly,
}) => {
  const buildingAlternative = subproject[tab];
  const selectedParams: TechnologyParameter[] = useMemo(
    () => buildingAlternative?.technologyParams['specialized']?.params || [],
    [buildingAlternative],
  );

  const [searchString, setSearchString] = useState('');
  const [sortState, setSortState] = useState<TechSortState>({
    dimension: TechSortDimensions[0],
    direction: 'asc',
  });
  const [filter, setFilter] = useState<EnergyKind[]>([]);

  const availableEnergyTypes = useMemo(() => {
    const types = new Set(
      specializedEquipment.map((e) => e.energySink).filter(Boolean),
    );
    return [...types] as EnergyKind[];
  }, [specializedEquipment]);

  // Filter available equipment based on current alternative and what's already selected
  const filterByAlternative = useCallback(
    (item: SpecializedEquipmentWithMetrics): boolean => {
      // Conventional: All technologies available
      if (tab === 'conventionalAlternative') {
        return true;
      }

      // Low Carbon: No gas
      if (tab === 'reducedCarbonAlternative') {
        return !item.energySink?.includes('Gas');
      }

      // Net Zero: No gas AND only low-energy electricity (≤2 icons)
      if (tab === 'zeroCarbonAlternative') {
        if (item.energySink?.includes('Gas')) {
          return false;
        }
        if (
          item.energySink === 'Electricity' &&
          item.relativeEnergyDemand > 2
        ) {
          return false;
        }
        return true;
      }

      return true;
    },
    [tab],
  );

  const availableEquipment = useMemo(() => {
    const filtered = specializedEquipment.filter((equipment) => {
      // Filter out already selected items
      const isSelected = selectedParams.some(
        (param) => param.techId === equipment.id,
      );
      if (isSelected) {
        return false;
      }
      // Filter by alternative constraints
      if (!filterByAlternative(equipment)) {
        return false;
      }
      // Search filter
      if (
        searchString.length > 0 &&
        !equipment.name.toLowerCase().includes(searchString.toLowerCase())
      ) {
        return false;
      }
      // Energy sink filter (include only selected energy types)
      if (filter.length > 0 && !filter.includes(equipment.energySink)) {
        return false;
      }
      return true;
    });
    return sortTechItemsByState(filtered, sortState);
  }, [
    specializedEquipment,
    selectedParams,
    filterByAlternative,
    searchString,
    filter,
    sortState,
  ]);

  const selectedEquipment = useMemo(() => {
    return selectedParams
      .map((param) => {
        const equipment = specializedEquipment.find(
          (e) => e.id === param.techId,
        );
        if (!equipment) {
          return null;
        }
        return { ...equipment, count: param.units };
      })
      .filter((item) => item !== null);
  }, [selectedParams, specializedEquipment]);

  // Calculate coverage, cost, and energy
  const metrics = useMemo(() => {
    // Capital comes from the stored category total, never local arithmetic
    // -- see storedCategoryCapitalCost for why. The tab badge reads the same.
    const cost = storedCategoryCapitalCost(
      subproject[tab],
      'specialized',
      costsArePending,
    );

    if (selectedEquipment.length === 0) {
      return { cost, energyDemand: 0, systemCoveragePercentage: 0 };
    }

    const energyDemand = selectedEquipment.reduce(
      (acc, item) =>
        acc + calculateEnergyDemand(item.energyUsePerYr, item.count),
      0,
    );

    const systemCoverageArea = selectedEquipment.reduce((acc, item) => {
      // Passive technologies have 0% coverage
      if (isPassiveTechnology(item)) {
        console.log(`${item.name} is passive, skipping coverage`);
        return acc;
      }
      const coverage = item.count / Math.max(1, item.limit);
      console.log(
        `${item.name}: count=${item.count}, limit=${item.limit}, coverage=${coverage}`,
      );
      // Coverage calculation: count / limit gives the fraction covered
      return acc + coverage;
    }, 0);

    return {
      cost,
      energyDemand,
      systemCoveragePercentage: systemCoverageArea,
    };
  }, [selectedEquipment, subproject, tab, costsArePending]);

  return (
    <DecarbContentLayout
      availableItems={
        readOnly
          ? []
          : availableEquipment.map((item) => (
              <SpecializedUnit
                key={item.id}
                equipmentUnit={item}
                selected={false}
                actions={{
                  onAdd: () => onAddEquipment(item.id, Number(item.limit)),
                }}
              />
            ))
      }
      selectedItems={selectedEquipment.map((item) => (
        <SpecializedUnit
          key={item.id}
          count={item.count}
          equipmentUnit={item}
          selected={true}
          isLoading={isLoading}
          readOnly={readOnly}
          actions={{
            onIncrement: () => onChangeEquipmentCount(item.id, item.count + 1),
            onDecrement: () => onChangeEquipmentCount(item.id, item.count - 1),
            onRemove: () => onChangeEquipmentCount(item.id, 0),
            onChangeCount: (newCount: number) =>
              onChangeEquipmentCount(item.id, newCount),
          }}
        />
      ))}
      overviewHeader={
        <DecarbOverviewHeader
          systemCoverageName={'Specialized Equipment'}
          systemCoverageIcon={<Settings />}
          systemCoverageHelperText={
            'Choose enough equipment to get 100% coverage'
          }
          energyDemand={metrics.energyDemand}
          capitalCost={metrics.cost}
          coveragePercentage={metrics.systemCoveragePercentage}
        />
      }
      helperText={emptyText}
      searchComponent={
        readOnly ? undefined : (
          <SearchBox query={searchString} setQuery={setSearchString} />
        )
      }
      filterComponent={
        readOnly ? undefined : availableEnergyTypes.length > 1 ? (
          <FilterMenu
            listOfAllOptions={availableEnergyTypes}
            selectedOptions={filter}
            onReset={() => setFilter([])}
            onSelect={(option: EnergyKind) =>
              setFilter((prev) =>
                prev.includes(option)
                  ? prev.filter((val) => val !== option)
                  : [...prev, option],
              )
            }
          />
        ) : undefined
      }
      sortComponent={
        readOnly ? undefined : (
          <SortMenu
            dimensions={TechSortDimensions}
            activeSort={sortState}
            onSortChange={setSortState}
          />
        )
      }
    />
  );
};
